import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HoverableImageLink } from "~/components/about-us/HoverableImageLink";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import styles from "../live-seo-page.module.css";

type ThankYouLocalization = {
	title: string;
	thanksTitle: string;
	introHeading: string;
	introCopy: string;
	doctorHeading: string;
	doctorCopy: string;
	educationHeading: string;
	educationCopy1: string;
	educationCopy2: string;
	communityHeading: string;
	communityCopy1: string;
	communityCopy2: string;
	roadHeading: string;
	roadCopy: string;
	smilingHeading: string;
	smilingCopy1: string;
	smilingCopy2: string;
	affiliationsHeading: string;
	affiliations: string[];
	ctas: Array<{
		label: string;
		href: string;
		image: string;
		position?: string;
	}>;
};

const thankYouLocalization = {
	[Locale.EN]: {
		title: "Thank You",
		thanksTitle: "Thanks",
		introHeading: "Thank you for contacting us.",
		introCopy:
			"A member of our friendly team will be in touch with you shortly. We look forward to seeing you soon!",
		doctorHeading: "Meet Dr. Garcia-Paul",
		doctorCopy:
			"Dr. Carmen Garcia-Paul was born and raised in Miami and—despite leaving the area for graduate studies—has always considered the area her home. She graduated from Our Lady of Lourdes Academy where she was both a scholar-athlete and valedictorian of her class. After Lourdes, Dr. Garcia-Paul was accepted into the honors program at the University of Miami. While an undergraduate, Dr. Garcia-Paul began working for several local dentists and dental specialists in Miami. It was during this time that she first became interested in the specialty of orthodontics. After seeing the positive effect that orthodontics could have on a person’s self-esteem, personality, and confidence, she decided to dedicate her life to helping others by perfecting their smiles.",
		educationHeading: "Education",
		educationCopy1:
			"After graduating from UM with honors, Dr. Garcia-Paul left her Miami home. Her graduate training first brought her to Philadelphia where she attended the University of Pennsylvania School of Dental Medicine and earned her doctorate in dental medicine. Following her graduation from Penn, Dr. Garcia-Paul was 1 of 7 accepted into the competitive orthodontic residency program at the University of Michigan. During her three years of specialty training, she won several scholarships and academic awards, including the Rackham School of Graduate Studies Award, the Colgate Palmolive/ Hispanic Dental Association Scholarship and an award from the American Cleft Palate-Craniofacial Association.",
		educationCopy2:
			"Staying current on state-of-the-art treatment methods is incredibly important to Dr. Garcia-Paul so she can provide you with the highest level of care. She attends continuing education courses and events throughout the year so she is current on the latest techniques available.",
		communityHeading: "Community Involvement",
		communityCopy1:
			"Throughout her educational journey, Dr. Garcia-Paul always found a way to give back to the community. She has volunteered with programs that offer free oral health screenings and dental services for children who could not afford treatment. In residency, she focused her master’s thesis on analyzing barriers to orthodontic treatment encountered by families of children with craniofacial anomalies.",
		communityCopy2:
			"Since moving back to Miami, she has been a volunteer on staff at Nicklaus Children’s Hospital Craniofacial Team and she is a volunteer instructor in the dental assisting program at the Robert Morgan Technical Institute. She is also a committed provider for Smiles Change Lives, a community program that provides much-needed orthodontic care to children in Miami. Since starting with the program she has donated over $130,000 of free orthodontic treatment to children in need.",
		roadHeading: "The Road Back to Miami",
		roadCopy:
			"After graduating from her orthodontics residency, Dr. Garcia-Paul moved to Rhode Island with her husband, Alex, who was then serving in the United States Navy. While he completed his tour of duty, she worked in New England as an orthodontist. After nine years away, she was excited to finally return to her Miami home.",
		smilingHeading: "Smiling In Miami",
		smilingCopy1:
			"Since returning home, Dr. Garcia-Paul has been able to transform hundreds of smiles by focusing on her dream of providing high-quality, personalized care. By being the sole orthodontist in Miami Orthodontic Specialists, she has been able to tailor her treatment with braces or Invisalign to the individual needs of her patients. Not only does her patient family continue to grow, but she is now the mother of two adventurous boys who keep her very busy outside the office.",
		smilingCopy2:
			"In addition to her passion for creating beautiful smiles, Dr. Garcia-Paul also enjoys eating donuts, salsa dancing, watching super hero movies, and exploring new places with her husband, Alex (whenever she can get a babysitter).",
		affiliationsHeading: "Professional Affiliations",
		affiliations: [
			"American Board of Orthodontics",
			"American Association of Orthodontists",
			"Southern Association of Orthdontists",
			"Florida Association of Orthodontists",
			"American Dental Association",
			"Florida Dental Association",
			"Miami Dade Dental Society",
			"Smiles Change Lives",
		],
		ctas: [
			{
				label: "Meet Our Doctors",
				href: "/meet-our-doctors",
				image: "/about-dr.jpg",
				position: "top",
			},
			{
				label: "Meet The Team",
				href: "/meet-the-team",
				image: "/meet-the-team-header-2026.jpg",
				position: "top",
			},
			{
				label: "Office Tour",
				href: "/office-tour",
				image: "/miami-ortho-3.jpg",
				position: "top",
			},
			{
				label: "Careers",
				href: "/careers",
				image: "/careers-1.jpg",
				position: "top",
			},
			{
				label: "Blog",
				href: "/blog",
				image: "/miami-ortho-12-1024x684.jpg",
				position: "50% 40%",
			},
		],
	},
	[Locale.ES]: {
		title: "Gracias",
		thanksTitle: "Gracias",
		introHeading: "Gracias por contactarnos.",
		introCopy:
			"Un miembro de nuestro amable equipo se comunicará contigo pronto. ¡Esperamos verte pronto!",
		doctorHeading: "Conozca a la Dra. Garcia-Paul",
		doctorCopy:
			"La Dra. Carmen Garcia-Paul nació y creció en Miami y, aunque se fue del área para realizar estudios de posgrado, siempre ha considerado a Miami su hogar. Se graduó de Our Lady of Lourdes Academy, donde fue estudiante-atleta y la mejor estudiante de su clase. Después de Lourdes, fue aceptada en el programa de honores de la Universidad de Miami. Mientras estudiaba, comenzó a trabajar con varios dentistas y especialistas dentales locales en Miami. Fue entonces cuando se interesó por primera vez en la ortodoncia. Después de ver el efecto positivo que la ortodoncia podía tener en la autoestima, personalidad y confianza de una persona, decidió dedicar su vida a ayudar a otros perfeccionando sus sonrisas.",
		educationHeading: "Educación",
		educationCopy1:
			"Después de graduarse de la UM con honores, la Dra. Garcia-Paul dejó su hogar en Miami. Su formación de posgrado la llevó primero a Filadelfia, donde asistió a la Facultad de Medicina Dental de la Universidad de Pensilvania y obtuvo su doctorado en medicina dental. Después de graduarse de Penn, fue una de siete personas aceptadas en el competitivo programa de residencia en ortodoncia de la Universidad de Michigan. Durante sus tres años de formación especializada, ganó varias becas y premios académicos, incluidos el Rackham School of Graduate Studies Award, la beca Colgate Palmolive/Hispanic Dental Association y un premio de la American Cleft Palate-Craniofacial Association.",
		educationCopy2:
			"Mantenerse al día con los métodos de tratamiento más avanzados es muy importante para la Dra. Garcia-Paul, para poder brindarte el más alto nivel de atención. Asiste a cursos y eventos de educación continua durante todo el año para mantenerse actualizada con las técnicas más recientes.",
		communityHeading: "Participación comunitaria",
		communityCopy1:
			"A lo largo de su trayectoria educativa, la Dra. Garcia-Paul siempre encontró una manera de contribuir a la comunidad. Ha sido voluntaria en programas que ofrecen exámenes gratuitos de salud oral y servicios dentales para niños que no podían pagar tratamiento. Durante su residencia, enfocó su tesis de maestría en analizar las barreras al tratamiento de ortodoncia que enfrentan las familias de niños con anomalías craneofaciales.",
		communityCopy2:
			"Desde que regresó a Miami, ha sido voluntaria en el equipo craneofacial de Nicklaus Children’s Hospital y es instructora voluntaria en el programa de asistencia dental de Robert Morgan Technical Institute. También es una proveedora comprometida con Smiles Change Lives, un programa comunitario que brinda atención ortodóncica muy necesaria a niños en Miami. Desde que comenzó con el programa, ha donado más de $130,000 en tratamiento de ortodoncia gratuito para niños necesitados.",
		roadHeading: "El camino de regreso a Miami",
		roadCopy:
			"Después de graduarse de su residencia en ortodoncia, la Dra. Garcia-Paul se mudó a Rhode Island con su esposo, Alex, quien en ese momento servía en la Marina de los Estados Unidos. Mientras él completaba su servicio, ella trabajó como ortodoncista en Nueva Inglaterra. Después de nueve años fuera, estaba emocionada de finalmente regresar a su hogar en Miami.",
		smilingHeading: "Sonriendo en Miami",
		smilingCopy1:
			"Desde que regresó a casa, la Dra. Garcia-Paul ha podido transformar cientos de sonrisas al enfocarse en su sueño de brindar atención personalizada y de alta calidad. Al ser la única ortodoncista en Miami Orthodontic Specialists, ha podido adaptar su tratamiento con brackets o Invisalign a las necesidades individuales de sus pacientes. Su familia de pacientes sigue creciendo, y ahora también es madre de dos niños aventureros que la mantienen muy ocupada fuera de la oficina.",
		smilingCopy2:
			"Además de su pasión por crear sonrisas hermosas, la Dra. Garcia-Paul disfruta comer donas, bailar salsa, ver películas de superhéroes y explorar nuevos lugares con su esposo, Alex (cuando consigue niñera).",
		affiliationsHeading: "Afiliaciones profesionales",
		affiliations: [
			"American Board of Orthodontics",
			"American Association of Orthodontists",
			"Southern Association of Orthdontists",
			"Florida Association of Orthodontists",
			"American Dental Association",
			"Florida Dental Association",
			"Miami Dade Dental Society",
			"Smiles Change Lives",
		],
		ctas: [
			{
				label: "Conozca a nuestros doctores",
				href: "/meet-our-doctors",
				image: "/about-dr.jpg",
				position: "top",
			},
			{
				label: "Conozca al equipo",
				href: "/meet-the-team",
				image: "/meet-the-team-header-2026.jpg",
				position: "top",
			},
			{
				label: "Tour de la oficina",
				href: "/office-tour",
				image: "/miami-ortho-3.jpg",
				position: "top",
			},
			{
				label: "Carreras",
				href: "/careers",
				image: "/careers-1.jpg",
				position: "top",
			},
			{
				label: "Blog",
				href: "/blog",
				image: "/miami-ortho-12-1024x684.jpg",
				position: "50% 40%",
			},
		],
	},
} satisfies Record<Locale, ThankYouLocalization>;

export function ThankYouPage({
	titleKey,
}: {
	titleKey: "title" | "thanksTitle";
}) {
	const getLocalization = useLocalization(thankYouLocalization);
	const ctas = getLocalization("ctas");

	return (
		<Flex direction="column">
		<HeroStyle2
				showFullImageOnMobile
				heroImageUrl="/about-dr.jpg"
				title={getLocalization(titleKey)}
				backgroundPosition="center"
			/>
			<Box className={styles.thankYouIntroBand}>
				<Box className={styles.thankYouInner}>
					<Heading as="h2" className={styles.thankYouIntroHeading}>
						{getLocalization("introHeading")}
					</Heading>
					<Text as="p" size="4" className={styles.thankYouIntroCopy}>
						{getLocalization("introCopy")}
					</Text>
				</Box>
			</Box>
			<Box className={styles.thankYouDoctorBand}>
				<Box className={styles.thankYouDoctorInner}>
					<section className={styles.thankYouDoctorGrid}>
						<OptimizedImage
							src="/Dr-Garcia-Paul.jpg"
							alt="Dr. Carmen Garcia-Paul smiling in a white coat, representing Miami Orthodontic Specialists."
							className={styles.thankYouDoctorImage}
						/>
						<Box className={styles.thankYouCopyBlock}>
							<Heading as="h3" className={styles.thankYouSectionHeading}>
								{getLocalization("doctorHeading")}
							</Heading>
							<Text as="p" size="4">
								{getLocalization("doctorCopy")}
							</Text>
						</Box>
					</section>
					<Grid
						columns={{ initial: "1", md: "2" }}
						gap="7"
						className={styles.thankYouDetailGrid}
					>
						<Box className={styles.thankYouCopyBlock}>
							<Heading as="h4" className={styles.thankYouSubheading}>
								{getLocalization("educationHeading")}
							</Heading>
							<Text as="p" size="4">
								{getLocalization("educationCopy1")}
							</Text>
							<Text as="p" size="4">
								{getLocalization("educationCopy2")}
							</Text>
						</Box>
						<Box className={styles.thankYouCopyBlock}>
							<Heading as="h4" className={styles.thankYouSubheading}>
								{getLocalization("communityHeading")}
							</Heading>
							<Text as="p" size="4">
								{getLocalization("communityCopy1")}
							</Text>
							<Text as="p" size="4">
								{getLocalization("communityCopy2")}
							</Text>
						</Box>
					</Grid>
					<OptimizedImage
						src="/carmen-garcia-paul-orthodontic.jpg"
						alt="Smiling family portrait featuring Dr. Carmen Garcia-Paul with her husband and their baby."
						className={styles.thankYouFamilyImage}
					/>
					<Box className={styles.thankYouCopyBlock}>
						<Heading as="h3" className={styles.thankYouSectionHeading}>
							{getLocalization("roadHeading")}
						</Heading>
						<Text as="p" size="4">
							{getLocalization("roadCopy")}
						</Text>
					</Box>
					<Grid
						columns={{ initial: "1", md: "2" }}
						gap="7"
						className={styles.thankYouDetailGrid}
					>
						<Box className={styles.thankYouCopyBlock}>
							<Heading as="h4" className={styles.thankYouSubheading}>
								{getLocalization("smilingHeading")}
							</Heading>
							<Text as="p" size="4">
								{getLocalization("smilingCopy1")}
							</Text>
							<Text as="p" size="4">
								{getLocalization("smilingCopy2")}
							</Text>
						</Box>
						<Box className={styles.thankYouCopyBlock}>
							<Heading as="h4" className={styles.thankYouSubheading}>
								{getLocalization("affiliationsHeading")}
							</Heading>
							<ul className={styles.thankYouAffiliations}>
								{getLocalization("affiliations").map((affiliation) => (
									<li key={affiliation}>{affiliation}</li>
								))}
							</ul>
						</Box>
					</Grid>
				</Box>
			</Box>
			<Box className={styles.thankYouCtaBand}>
				<Flex wrap="wrap" className={styles.thankYouCtaGrid}>
					{ctas.map((cta) => (
						<Box className={styles.thankYouCtaItem} key={cta.href}>
							<HoverableImageLink
								imageUrl={cta.image}
								label={cta.label}
								to={cta.href}
								backgroundPosition={cta.position}
							/>
						</Box>
					))}
				</Flex>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
